import React, { useEffect, useState, memo } from "react";
import { Bar } from "react-chartjs-2";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

Chart.register(CategoryScale, ChartDataLabels);

const priceFormat = (price) => {
  return price.toLocaleString("en-US", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  });
};

export const SectorBasedHourlyReport = memo(({}) => {
  const { t } = useTranslation();
  var currentTime = new Date();
  var currentyear = currentTime.getFullYear();
  var currentMonth = currentTime.getUTCMonth() + 1;
  const [filter, setFilter] = useState(0);
  const [totalamt, setTotalamt] = useState(0);
  const [totalcnt, setTotalcnt] = useState(0);
  const [typeFilter, setTypeFilter] = useState("Qty Sold");
  const [filtertext, setFilterText] = useState("");
  const [filterDateType, setFilterDateType] = useState("OrderedDate");
  const [isLoading, setIsLoading] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [sectorList, setSectorList] = useState([]);
  const [sector, setSector] = useState("All");
  const getCurrentFiscalYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    // Fiscal year starts in September
    const startYear = month >= 9 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
  };
  const [period, setPeriod] = useState(getCurrentFiscalYear());
  const [months, setMonths] = useState("9");
  const [quarter, setQuarter] = useState("");
  const MonthNames = [
    t("months.sep"),
    t("months.oct"),
    t("months.nov"),
    t("months.dec"),
    t("months.jan"),
    t("months.feb"),
    t("months.mar"),
    t("months.apr"),
    t("months.may"),
    t("months.jun"),
    t("months.jul"),
    t("months.aug"),
  ];
  const currentFiscalMonth = new Date().getMonth(); // 0-indexed: Jan = 0, Apr = 3, Sep = 8

  const dayNames = [
    t("days.mon"),
    t("days.tue"),
    t("days.wed"),
    t("days.thu"),
    t("days.fri"),
    t("days.sat"),
    t("days.sun"),
  ];
  const MondayToSundayOrder = [2, 3, 4, 5, 6, 7, 1]; // Monday to Sunday

  const quarterNames = {
    1: t("quarter_sep_nov"),
    2: t("quarter_dec_feb"),
    3: t("quarter_mar_may"),
    4: t("quarter_jun_aug"),
  };

  const getCurrentFiscalQuarter = () => {
    const month = new Date().getMonth() + 1; // 1–12
    if (month >= 9 && month <= 11) return 1; // Sep–Nov
    if (month === 12 || month <= 2) return 2; // Dec–Feb
    if (month >= 3 && month <= 5) return 3; // Mar–May
    return 4; // Jun–Aug
  };

  const getAvailableQuarters = (selectedFiscalYear) => {
    const currentFiscalYear = getCurrentFiscalYear();
    const currentQuarter = getCurrentFiscalQuarter();
    return Object.entries(quarterNames)
      .filter(([quarterNumber]) => {
        if (selectedFiscalYear === currentFiscalYear) {
          return Number(quarterNumber) <= currentQuarter;
        }
        return true; // show all for past years
      })
      .map(([quarterNumber, label]) => (
        <option key={quarterNumber} value={quarterNumber}>
          {label}
        </option>
      ));
  };

  useEffect(() => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const fiscalQuarterMap = {
      9: 1,
      10: 1,
      11: 1,
      12: 2,
      1: 2,
      2: 2,
      3: 3,
      4: 3,
      5: 3,
      6: 4,
      7: 4,
      8: 4,
    };
    const currentFiscalQuarter = fiscalQuarterMap[month];
    setQuarter(currentFiscalQuarter);
  }, []);
  // Custom plugin to display "No data found"
  const noDataPlugin = {
    id: "noDataPlugin",
    beforeDraw: (chart) => {
      const { datasets } = chart.data;
      const hasData = datasets.some((dataset) => dataset.data.length > 0);
      if (!hasData) {
        const ctx = chart.ctx;
        const { width, height } = chart;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "16px Arial";
        ctx.fillStyle = "#eb9f9d";
        ctx.fillText(t("no_data_filtered_search_txt"), width / 2, height / 2);
        ctx.restore();
      }
    },
  };

  const [topSellerChartData, setTopSellerChartData] = useState({
    labels: [],
    datasets: [
      {
        label: t("top_selling_products_txt"),
        data: [],
        backgroundColor: ["rgba(252,194,84,1)", "#f4ecec"],
        borderColor: "#e6e6e6",
        borderWidth: 2,
      },
    ],
  });

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      let URL = BaseUrl + "admin/product/masterentries/sectors";
      const response = await http.get(URL);
      const data = response.data.data;
      setSectorList(data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    if (filter === "custom" && (!fromDate || !toDate)) {
      setIsLoading(false);
      // Don't make the API call if dates are missing for the "custom" filter
      return;
    }
    let payload = {};
    if (filter === "custom") {
      payload = {
        filter: filter,
        datefrom: format(new Date(fromDate), "yyyy-MM-dd"),
        dateto: format(new Date(toDate), "yyyy-MM-dd"),
        sector: sector,
        filterType: filterDateType,
      };
      setShowDatePicker(false);
    } else if (["weekly", "monthly", "quarterly", "FYdata"].includes(filter)) {
      payload = {
        filter: filter,
        datefrom: "",
        dateto: "",
        period: period,
        sector: sector,
        filterType: filterDateType,
      };
    } else {
      payload = {
        filter: parseInt(filter),
        datefrom: "",
        dateto: "",
        sector: sector,
        filterType: filterDateType,
      };
    }
    let URL = BaseUrl + "admin/promo-pinsa-daywise-report";
    http
      .post(URL, payload)
      .then((res) => res.data.data)
      .then((response) => {
        setIsLoading(false);
        const actualMonth = parseInt(months);
        let filteredData = [];
        if (filter === "monthly") {
          filteredData = response.filter(
            (item) => item._id.month === actualMonth,
          );
        } else if (filter === "quarterly") {
          filteredData = response.filter(
            (item) => item._id.fiscalQuarter === quarter,
          );
        } else {
          filteredData = response;
        }
        // Create a map of revenue by day
        const revenueMap = {};
        filteredData.forEach((item) => {
          const day = item._id?.dayOfWeek || item._id?.day || item._id; // Handle both nested and flat structures
          revenueMap[day] = item;
        });
        // Filter out 0 revenue days and keep order from Monday to Sunday
        const orderedData = MondayToSundayOrder.filter(
          (day) => revenueMap[day] && revenueMap[day].totalAmount > 0,
        ).map((day) => ({
          label: dayNames[day === 1 ? 6 : day - 2], // Convert day number to day name (Monday-Sunday)
          totalAmount: revenueMap[day].totalAmount,
          orderCount: revenueMap[day].orderCount,
        }));
        const totalAmount = orderedData.reduce(
          (sum, d) => sum + d.totalAmount,
          0,
        );
        const totalCount = orderedData.reduce(
          (sum, d) => sum + d.orderCount,
          0,
        );
        setTotalamt(totalAmount);
        setTotalcnt(totalCount);
        setTopSellerChartData({ labels: [], datasets: [] });
        setTopSellerChartData({
          labels: orderedData.map((d) => d.label),
          datasets: [
            {
              label:
                typeFilter === "Qty Sold"
                  ? t("order_count_label_txt")
                  : t("total_revenue_txt"),
              data: orderedData.map((item) =>
                typeFilter === "Qty Sold"
                  ? item.orderCount
                  : item.totalAmount.toFixed(2),
              ),
              backgroundColor: ["#8dc5e4", "#1b81bc"],
              borderColor: "#E5E5E5",
              borderWidth: 0,
              barThickness: 40,
              maxBarThickness: 60,
              borderRadius: 4,
            },
          ],
        });
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  }, [filter, fromDate, toDate, typeFilter, months, quarter, period, sector, filterDateType]);

  const DateChange = (value, type) => {
    if (type === "from") {
      setFromDate(value);
      if (toDate && value && value > toDate) {
        setToDate(null);
      }
    } else if (type === "to") {
      if (!fromDate || value >= fromDate) {
        setToDate(value);
      } else {
        // Handle invalid date selection
        setToDate(null);
      }
    }
  };

  const changeFilterHandler = (e) => {
    setFilter(e.target.value);
    setFilterText(e.target.options[e.target.selectedIndex].text);
    if (e.target.value === "custom") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      setFromDate(null);
      setToDate(null);
      setFilter(e.target.value);
    }
  };

  const changePeriodHandler = (e) => {
    const newPeriod = e.target.value;
    setPeriod(newPeriod); // update selected fiscal year in state
    //split fiscal year
    const [newStartYear, newEndYear] = newPeriod.split("-").map(Number);
    const fiscalStartMonthIndex = 8; // 0-based index for September
    const today = new Date();
    const currentFiscalYear = getCurrentFiscalYear();
    //compare sleted year with currentfiscalyear
    const isCurrentFiscalYear = newPeriod === currentFiscalYear;
    // MONTHLY filter logic
    if (filter === "monthly") {
      let visibleMonthNames = MonthNames;
      if (isCurrentFiscalYear) {
        // Calculate current fiscal month index
        const currentMonthIndex = today.getMonth(); // 0-based
        const monthsPassedSinceFiscalStart =
          (currentMonthIndex - fiscalStartMonthIndex + 12) % 12;
        const maxVisibleIndex = monthsPassedSinceFiscalStart;
        visibleMonthNames = MonthNames.slice(0, maxVisibleIndex + 1);
      }
      const selectedMonthIndex = MonthNames.indexOf(months);
      const isValidMonth =
        selectedMonthIndex >= 0 &&
        selectedMonthIndex < visibleMonthNames.length;
      if (!isValidMonth) {
        const defaultMonthNumber = ((0 + fiscalStartMonthIndex) % 12) + 1;
        setMonths(String(defaultMonthNumber));
      }
    }
    // QUARTERLY filter logic
    if (filter === "quarterly") {
      const currentQuarter = getCurrentFiscalQuarter();
      let maxQuarter = 4;
      if (isCurrentFiscalYear) {
        maxQuarter = currentQuarter;
      }
      if (parseInt(quarter) > maxQuarter) {
        setQuarter(maxQuarter > 0 ? String(maxQuarter) : "1");
      }
    }
  };

  const changeMonthHandler = (e) => {
    setMonths(e.target.value);
  };

  const formatDate = (dateString) => {
    const dateObject = new Date(dateString);
    const year = dateObject.getFullYear();
    const month = String(dateObject.getMonth() + 1).padStart(2, "0");
    const day = String(dateObject.getDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  };

  const options = [];
  for (let i = currentyear; i >= 2023; i--) {
    if (i === currentyear && currentMonth < 9) {
      continue;
    }
    const year = i;
    options.push(
      <option value={year + "-" + (year + 1)}>
        {year + "-" + (year + 1)}
      </option>,
    );
  }

  const currentFiscalYear = getCurrentFiscalYear();
  let visibleMonthNames = MonthNames;
  if (period === currentFiscalYear) {
    // Limit to current fiscal month
    const fiscalIndex = (new Date().getMonth() - 8 + 12) % 12; // Sep=0, Oct=1, ... Aug=11
    visibleMonthNames = MonthNames.slice(0, fiscalIndex + 1);
  }

  let loadingbuffer = isLoading ? "loading-buffer" : "";

  return (
    <div className={`chart-container chart-category-sales ${loadingbuffer}`}>
      <div className="chart-Hdr">
        <div className="chart-Hdr-txt">
          <h2 style={{ textAlign: "left" }}>
            {t("sector_based_free_offer_daywise_analysis_txt")}
          </h2>
        </div>
        <div className="chart-fltr-hldr">
          <div className="chart-fltr">
            <select value={sector} onChange={(e) => setSector(e.target.value)}>
              <option value="All">{t("all_txt")}</option>
              {sectorList.map((item) => (
                <option key={item._id} value={item.MasterValue}>
                  {item.MasterValue}
                </option>
              ))}
            </select>
          </div>
          <div className="chart-fltr">
            <select
              value={filterDateType}
              onChange={(e) => setFilterDateType(e.target.value)}
            >
              <option value={"OrderedDate"}>{t("ordered_date_txt")}</option>
              <option value={"deliveredDate"}>{t("delivery_date_txt")}</option>
            </select>
          </div>
          <div
            className="chart-fltr"
            onMouseEnter={() => {
              if (filter === "custom" && !showDatePicker) {
                setShowDatePicker(true);
              }
            }}
            onMouseLeave={() => {
              if (filter === "custom" && showDatePicker) {
                setShowDatePicker(false);
              }
            }}
          >
            {
              <select value={filter} onChange={(e) => changeFilterHandler(e)}>
                <option value={0}>{t("all_txt")}</option>
                <option value={"weekly"}>{t("current_week_txt")}</option>
                <option value={"monthly"}>{t("monthly_txt")}</option>
                <option value={"quarterly"}>{t("quarterly_txt")}</option>
                <option value={"FYdata"}>{t("fy_data_txt")}</option>
                <option value={30}>{t("past_30_days_txt")}</option>
                <option value={90}>{t("past_90_days_txt")}</option>
                <option value={180}>{t("past_180_days_txt")}</option>
                <option value={365}>{t("past_1_year_txt")}</option>
                <option value={"custom"}>{t("custom_date_txt")}</option>
              </select>
            }
            {showDatePicker && (
              <div className="date-picker-container">
                <label>{t("from_label_txt")}</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(date) => DateChange(date, "from")}
                  showDisabledMonthNavigation
                  dateFormat="dd/MM/yyyy"
                  disablePast={true}
                  maxDate={
                    filterDateType === "deliveredDate"
                      ? new Date(
                          new Date().getFullYear(),
                          new Date().getMonth() + 1,
                          new Date().getDate(),
                        )
                      : new Date()
                  }
                  placeholderText={t("select_date_txt")}
                />
                <label>{t("to_label_txt")}</label>
                <DatePicker
                  selected={toDate}
                  showDisabledMonthNavigation
                  dateFormat="dd/MM/yyyy"
                  onChange={(date) => DateChange(date, "to")}
                  disablePast={true}
                  maxDate={
                    filterDateType === "deliveredDate"
                      ? new Date(
                          new Date().getFullYear(),
                          new Date().getMonth() + 1,
                          new Date().getDate(),
                        )
                      : new Date()
                  }
                  placeholderText={t("select_date_txt")}
                />
              </div>
            )}
            {filter === "quarterly" && (
              <select
                style={{ width: "160px" }}
                value={quarter}
                onChange={(e) => setQuarter(Number(e.target.value))}
              >
                {getAvailableQuarters(period)}
              </select>
            )}
            {filter === "monthly" && (
              <select
                style={{ width: "50px" }}
                value={months}
                onChange={(e) => changeMonthHandler(e)}
              >
                {visibleMonthNames.map((monthName, index) => {
                  const monthNumber = ((index + 8) % 12) + 1; // fiscal-to-calendar month number
                  return (
                    <option key={monthNumber} value={monthNumber}>
                      {monthName}
                    </option>
                  );
                })}
              </select>
            )}
            {(filter === "monthly" ||
              filter === "quarterly" ||
              filter === "FYdata") && (
              <select
                style={{ width: "170px" }}
                value={period}
                onChange={(e) => changePeriodHandler(e)}
              >
                {options}
              </select>
            )}
          </div>
        </div>
        <div className="fltr-sub-val">
          <span className="fltr-val">
            {typeFilter === "Qty Sold"
              ? t("total_order_count_text_txt") + totalcnt
              : t("total_amount_text_txt") + priceFormat(totalamt)}
          </span>
          <span className="fltr-val">
            {[t("current_week_txt"), t("monthly_txt"), t("quarterly_txt"), t("fy_data_txt")].includes(
              filtertext,
            )
              ? ""
              : filtertext != "" && filtertext != t("custom_date_txt")
                ? " (" + filtertext + ")"
                : filtertext === t("custom_date_txt") && fromDate && toDate
                  ? " (" +
                    formatDate(fromDate) +
                    " - " +
                    formatDate(toDate) +
                    ")"
                  : " (" + t("all_txt") + ")"}
          </span>
        </div>
      </div>
      {topSellerChartData && (
        <Bar
          data={topSellerChartData}
          plugins={[noDataPlugin]}
          options={{
            scales: {
              y: {
                grid: {
                  color: "rgba(247, 243, 241, 0.8)",
                },
                ticks: {
                  color: "rgba(207, 208, 211, 1)",
                },
              },
              x: {
                grid: {
                  color: "rgba(247, 243, 241, 0.8)",
                },
                ticks: {
                  color: "rgba(207, 208, 211, 1)",
                  maxRotation: 20, // Keep labels readable
                  minRotation: 20,
                  callback: function (value, index, ticks) {
                    const label = this.getLabelForValue(value); // Get the label for the current tick
                    if (typeof label === "string" && label.includes(" ")) {
                      const [date, dayOfWeek] = label.split(" "); // Split by the comma separator
                      return [dayOfWeek, date]; // Return the label with a newline
                    }
                    return label; // Single-line label for other cases
                  },
                },
              },
            },
            layout: {
              padding: {
                top: 40,
                right: 0,
                bottom: 0,
                left: 0,
              },
            },
            plugins: {
              title: {
                display: false,
                text: t("revenue_analysis_txt"),
              },
              legend: {
                display: false,
              },
              tooltip: {
                titleColor: "rgba(220, 222, 226, 1)",
                bodyColor: "rgba(220, 222, 226, 1)",
              },
              datalabels: {
                display: true,
                color: "#523b3b",
                font: {
                  size: 11,
                  weight: "600",
                },
                padding: 5,
                anchor: "end",
                align: "end",
                offset: 10,
                rotation: 0,
                clip: false,
                formatter: function (value, context) {
                  const orderNum = context.dataIndex + 1;
                  const formattedValue =
                    typeFilter === "Qty Sold"
                      ? `${value}`
                      : priceFormat(parseFloat(value));
                  return `${t("order#_number_txt")}: ${formattedValue}`;
                },
              },
            },
          }}
        />
      )}
    </div>
  );
});
